/**
 * The deal-level roll-up of a portfolio's property models.
 *
 * Each property carries its own Sponsor-side and client-side (AREP) models; this is
 * what they say about the DEAL, side by side, with the per-property breakdown
 * beneath. The rules it shows are the server's (services/uw_rollup.py):
 * additive figures sum, ratios are the ratio of the summed legs, and a figure
 * is the deal's only when every property contributes. When one does not, the
 * deal figure is withheld, what the others give is labelled partial, and the
 * property that is the gap is named. Nothing here fills a gap from the other
 * side or averages across it.
 *
 * `summary` is the dashboard card; `full` is the Model Review tab, with the
 * models behind each side, the sources of every figure and the table by
 * property.
 */

import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { AlertTriangle, ArrowRight, ChevronDown, ChevronRight, Loader2 } from 'lucide-react'

import { useSideLabels } from './sideLabels'
import {
  UW_ROLLUP_KEY,
  uwPropertyModelsService,
  type RolledFigure,
  type RollupContributor,
  type RollupMissing,
  type RollupSide,
} from '@/services/uwPropertyModels'
import { formatAmount } from './format'
import { EmptyLine } from './ui'

const DASH = '—'

/** The figures the dashboard card shows, in this order, when a side states them. */
const SUMMARY_FIELDS = [
  'loan_amount', 'property_value', 'total_cost', 'noi', 'annual_debt_service',
  'cap_rate', 'ltv', 'ltc', 'dscr', 'debt_yield', 'occupancy',
]
/** The figures the by-property table carries. */
const BREAKDOWN_FIELDS = ['noi', 'property_value', 'loan_amount', 'annual_debt_service', 'cap_rate', 'ltv', 'dscr']

/** What a property's missing side is called in the by-property table, by why it is missing. */
const GAP_LABEL: Record<RollupMissing['reason'], (side: string) => string> = {
  no_model: (side) => `No ${side} model`,
  not_eligible: (side) => `${side} model not eligible`,
  not_stated: (side) => `${side} model does not state it`,
  duplicate_model: (side) => `${side} model shared with another property`,
}

function errorDetail(err: unknown, fallback: string): string {
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
  return typeof detail === 'string' && detail.trim() ? detail : fallback
}

function exactMoney(value: number): string {
  const places = Number.isInteger(value) ? 0 : 2
  const body = Math.abs(value).toLocaleString('en-US', { minimumFractionDigits: places, maximumFractionDigits: places })
  return value < 0 ? `-$${body}` : `$${body}`
}

/** A rolled value in its own unit. Percents arrive on the 0-100 scale. */
function formatRolled(value: number, figure: { kind: string; unit: string | null }, exact: boolean): string {
  if (figure.kind === 'currency') return exact ? exactMoney(value) : formatAmount(value)
  if (figure.kind === 'percent') return `${Number(value.toFixed(2))}%`
  if ((figure.unit ?? '').toLowerCase() === 'x') return `${Number(value.toFixed(2))}x`
  return Number.isInteger(value) ? value.toLocaleString('en-US') : value.toLocaleString('en-US', { maximumFractionDigits: 2 })
}

/** The client's side against the sponsor, only where both are the deal's own figure. */
function difference(sponsor: RolledFigure | undefined, antarctica: RolledFigure | undefined): string {
  if (!sponsor || !antarctica || sponsor.value == null || antarctica.value == null) return DASH
  const diff = antarctica.value - sponsor.value
  const sign = diff >= 0 ? '+' : '-'
  if (sponsor.kind === 'percent') return `${sign}${Math.abs(diff).toFixed(2)} pts`
  if ((sponsor.unit ?? '').toLowerCase() === 'x') return `${sign}${Math.abs(diff).toFixed(2)}x`
  if (sponsor.value === 0) return DASH
  return `${sign}${Math.abs((diff / Math.abs(sponsor.value)) * 100).toFixed(1)}%`
}

function gapSentence(side: RollupSide): string {
  const named = side.missing.map((m) =>
    m.reason === 'no_model' ? `${m.name} has no ${side.label} model` : `${m.name}: ${m.detail}`,
  )
  return `${side.label}: ${side.coverage.have} of ${side.coverage.of} properties. ${named.join('; ')}.`
}

function FigureCell({ figure, exact, showMethod }: { figure: RolledFigure | undefined; exact: boolean; showMethod: boolean }) {
  if (!figure) return <span className="text-stone-700">{DASH}</span>
  if (figure.value != null) {
    return (
      <div>
        <span className="font-mono text-stone-100">{formatRolled(figure.value, figure, exact)}</span>
        {showMethod ? <div className="text-[8px] leading-tight text-stone-600">{figure.method}</div> : null}
        {figure.recordValue != null && figure.delta && !figure.delta.agrees ? (
          <div className="text-[8px] leading-tight text-amber-300/90" title="The roll-up and the deal record disagree. Neither is overwritten.">
            deal record {formatRolled(figure.recordValue, figure, exact)}
          </div>
        ) : null}
      </div>
    )
  }
  const names = figure.missing.map((m) => m.name).join(', ')
  return (
    <div title={names ? `Not the deal's figure. Missing: ${names}` : undefined}>
      <span className="text-stone-600">{DASH}</span>
      <div className="text-[8.5px] leading-tight text-amber-300/90">
        {figure.partialValue != null
          ? `partial ${formatRolled(figure.partialValue, figure, exact)} · ${figure.coverage.have} of ${figure.coverage.of}`
          : figure.missing[0]?.detail ?? 'not stated'}
      </div>
      {showMethod ? <div className="text-[8px] leading-tight text-stone-600">{figure.method}</div> : null}
    </div>
  )
}

/** Where one property's number on one side came from, or why there is none. */
function Provenance({
  figure,
  side,
  propertyId,
}: {
  figure: RolledFigure | undefined
  side: RollupSide
  propertyId: string
}) {
  const contributor: RollupContributor | undefined = figure?.contributors.find((c) => c.propertyId === propertyId)
  if (!figure || !contributor) {
    const gap: RollupMissing | undefined =
      figure?.missing.find((m) => m.propertyId === propertyId) ?? side.missing.find((m) => m.propertyId === propertyId)
    return <span className="text-amber-300/90">{gap?.detail ?? `${figure ? 'Not stated' : 'No figure'}`}</span>
  }
  const cell = (s: { filename: string | null; sheet: string | null; cell: string | null }) =>
    `${s.filename ?? 'model'} · ${s.sheet ?? ''}!${s.cell ?? ''}`
  const legs = contributor.inputs.map((i) => `${i.label} ${formatRolled(i.value, i, true)} (${cell(i.source)})`)
  // A ratio is rolled from its two legs and a weighted figure from its weight, whether or
  // not the property also states the figure itself, so the legs are shown either way.
  const made = legs.length === 0 ? null : legs.length === 1 ? 'weighted by' : contributor.source ? 'rolled from' : 'computed from'
  return (
    <div>
      <span className="font-mono text-stone-100">{formatRolled(contributor.value, figure, true)}</span>
      {contributor.source ? <span className="ml-1.5 text-stone-500">{cell(contributor.source)}</span> : null}
      {made ? <div className="text-stone-500">{made} {legs.join(' ÷ ')}</div> : null}
      {!contributor.rolled ? (
        <span className="ml-1.5 text-amber-300/90">stated, but not rolled up: its legs are not both stated</span>
      ) : null}
    </div>
  )
}

export function PortfolioRollupPanel({
  loanId,
  variant,
  onOpenFull,
}: {
  loanId: string
  variant: 'summary' | 'full'
  /** From the dashboard card into the full roll-up. */
  onOpenFull?: () => void
}) {
  const [open, setOpen] = useState<Set<string>>(new Set())
  const sideLabels = useSideLabels()
  const query = useQuery({
    queryKey: UW_ROLLUP_KEY(loanId),
    queryFn: () => uwPropertyModelsService.rollup(loanId),
    // The server reads every property's model to answer this, so it is read when it is
    // stale or after a change made here (every writer invalidates it), not on every visit.
    staleTime: 30_000,
  })

  const rollup = query.data
  const sponsor = rollup?.sides.find((s) => s.role === 'sponsor')
  const antarctica = rollup?.sides.find((s) => s.role === 'underwriter')

  const rows = useMemo(() => {
    const byField = new Map<string, { field: string; label: string; kind: string; unit: string | null; group: string; s?: RolledFigure; a?: RolledFigure }>()
    for (const [role, side] of [['s', sponsor], ['a', antarctica]] as const) {
      for (const figure of side?.figures ?? []) {
        const row = byField.get(figure.field) ?? {
          field: figure.field, label: figure.label, kind: figure.kind, unit: figure.unit, group: figure.group,
        }
        row[role] = figure
        byField.set(figure.field, row)
      }
    }
    const all = [...byField.values()]
    if (variant === 'full') return all
    return SUMMARY_FIELDS.map((name) => byField.get(name)).filter((row): row is NonNullable<typeof row> => Boolean(row))
  }, [sponsor, antarctica, variant])

  if (query.isLoading) {
    return (
      <div className="flex items-center gap-1.5 py-3 text-[10.5px] text-stone-500">
        <Loader2 size={12} className="animate-spin" /> Rolling up the property models…
      </div>
    )
  }
  if (query.isError) {
    return <p role="alert" className="py-2 text-[10.5px] text-red-300">{errorDetail(query.error, 'The roll-up could not be read.')}</p>
  }
  if (!rollup || !rollup.isPortfolio || !sponsor || !antarctica) return null

  const exact = variant === 'full'
  const nothingYet = sponsor.coverage.have === 0 && antarctica.coverage.have === 0
  const sides = [sponsor, antarctica]
  // Nothing counted is "no model" only when none is filed. A model that is withheld (one
  // workbook for several properties) or not eligible is filed, and why it is not counted
  // is what the person needs, so that is shown rather than an invitation to add a model.
  const filed = sides.some((s) => s.basis.some((b) => b.model) || s.missing.some((m) => m.reason !== 'no_model'))
  const gapSides = sides.filter((s) => s.missing.length > 0 && s.coverage.of > 0)
  const warnings = sides.flatMap((s) => s.warnings.map((w) => `${s.label}: ${w}`))
  const unread = sides.flatMap((s) => s.unread.map((u) => ({ ...u, side: s.label })))

  const toggle = (field: string) =>
    setOpen((current) => {
      const next = new Set(current)
      if (next.has(field)) next.delete(field)
      else next.add(field)
      return next
    })

  if (nothingYet && !filed) {
    return (
      <EmptyLine>
        No property has a model on either side yet. Use + Add UW Model on a property to start; the roll-up reads one model
        per property and side.
      </EmptyLine>
    )
  }

  const groups: string[] = []
  for (const row of rows) if (!groups.includes(row.group)) groups.push(row.group)
  const columns = variant === 'full' ? 4 : 3
  const breakdownColumns = BREAKDOWN_FIELDS.map((name) => rows.find((r) => r.field === name)).filter(
    (row): row is NonNullable<typeof row> => Boolean(row),
  )

  return (
    <div className="space-y-2.5">
      {variant === 'full' ? (
        <section aria-label="Models behind the roll-up" className="grid gap-2 md:grid-cols-2">
          {sides.map((side) => (
            <div key={side.role} className="rounded-md border border-stone-800 bg-black/20 p-2">
              <div className="mb-1 text-[9px] uppercase tracking-wider text-stone-500">
                {side.label} · {side.coverage.have} of {side.coverage.of} properties
              </div>
              <ul className="space-y-0.5 text-[9.5px]">
                {side.basis.map((b) => (
                  <li key={b.propertyId} className="flex flex-wrap items-baseline justify-between gap-x-2">
                    <span className="text-stone-300">{b.name}</span>
                    {b.model ? (
                      <span className="truncate text-stone-500" title={b.model.filename}>
                        {b.model.displayName} · v{b.model.versionNumber}
                        {b.figuresStated ? ` · ${b.figuresStated} figures` : ''}
                      </span>
                    ) : (
                      <span className="text-amber-300/90">
                        {side.missing.find((m) => m.propertyId === b.propertyId)?.detail ?? 'No model'}
                      </span>
                    )}
                    {b.withheld ? (
                      <span className="basis-full text-[9px] leading-snug text-amber-300/90">Withheld: {b.withheld.detail}</span>
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </section>
      ) : null}

      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-left text-[10px]" aria-label="Deal roll-up">
          <thead>
            <tr className="font-mono text-[8px] uppercase tracking-wider text-stone-500">
              <th className="border-b border-stone-800 px-2 py-1.5 font-normal">Figure</th>
              {sides.map((side) => (
                <th key={side.role} className="border-b border-stone-800 px-2 py-1.5 font-normal">
                  <div>{side.label}</div>
                  <div className={`normal-case tracking-normal ${side.coverage.have === side.coverage.of ? 'text-emerald-300/90' : 'text-amber-300/90'}`}>
                    {side.coverage.have} of {side.coverage.of} properties
                  </div>
                </th>
              ))}
              {variant === 'full' ? (
                <th className="border-b border-stone-800 px-2 py-1.5 font-normal">{sideLabels.underwriter} vs sponsor</th>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {groups.map((group) => (
              <GroupRows
                key={group}
                group={group}
                showGroup={variant === 'full'}
                columns={columns}
                rows={rows.filter((r) => r.group === group)}
                variant={variant}
                exact={exact}
                sides={sides}
                properties={rollup.properties}
                open={open}
                onToggle={toggle}
              />
            ))}
          </tbody>
        </table>
      </div>

      {gapSides.length > 0 ? (
        <div role="note" className="space-y-1 rounded border border-amber-400/20 bg-amber-400/[0.05] px-2.5 py-2 text-[9.5px] leading-relaxed text-amber-100/90">
          {nothingYet ? <p>No property’s model is counted in the roll-up yet.</p> : null}
          {gapSides.map((side) => (
            <p key={side.role}>{gapSentence(side)}</p>
          ))}
          <p className="text-amber-100/70">
            A side’s figure is the deal’s only when every property is covered, so those figures are withheld and a partial
            subtotal is labeled as one. Nothing is filled in from the other side.
          </p>
        </div>
      ) : null}

      {warnings.map((w) => (
        <p key={w} className="flex gap-1.5 text-[9.5px] leading-relaxed text-amber-300">
          <AlertTriangle size={11} className="mt-0.5 shrink-0" aria-hidden /> {w}
        </p>
      ))}
      {unread.map((u) => (
        <p key={`${u.side}-${u.propertyId}`} className="text-[9.5px] leading-relaxed text-amber-300/90">
          {u.name} ({u.side}): {u.displayName} is filed but nothing in it reached the roll-up
          ({u.mappedCellCount.toLocaleString()} of {u.importedCellCount.toLocaleString()} cells mapped).
        </p>
      ))}
      {rollup.excluded.length > 0 ? (
        <p className="text-[9.5px] text-stone-500">
          Left out of the roll-up, dropped from the deal: {rollup.excluded.map((e) => e.name).join(', ')}.
        </p>
      ) : null}

      {variant === 'summary' ? (
        onOpenFull ? (
          <button
            type="button"
            onClick={onOpenFull}
            className="inline-flex items-center gap-1 text-[10px] text-amber-300 transition-colors hover:text-amber-200"
          >
            Full roll-up <ArrowRight size={11} aria-hidden />
          </button>
        ) : null
      ) : (
        <section aria-label="By property" className="overflow-x-auto">
          <div className="mb-1 text-[9px] uppercase tracking-wider text-stone-500">By property</div>
          <table className="w-full border-collapse text-left text-[10px]" aria-label="Figures by property">
            <thead>
              <tr className="font-mono text-[8px] uppercase tracking-wider text-stone-500">
                <th className="border-b border-stone-800 px-2 py-1.5 font-normal">Property</th>
                {breakdownColumns.map((c) => (
                  <th key={c.field} className="border-b border-stone-800 px-2 py-1.5 text-right font-normal">{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rollup.properties.map((p) => (
                <tr key={p.id} className="border-t border-stone-900 align-top">
                  <td className="px-2 py-1.5">
                    <div className="text-stone-200">{p.name}</div>
                    <div className="text-[9px] text-stone-500">{p.location}</div>
                    {sides.map((side) => {
                      const gap = side.missing.find((m) => m.propertyId === p.id)
                      return gap ? (
                        <div key={side.role} className="text-[8.5px] text-amber-300/90">
                          {GAP_LABEL[gap.reason](side.label)}
                        </div>
                      ) : null
                    })}
                  </td>
                  {breakdownColumns.map((c) => (
                    <td key={c.field} className="px-2 py-1.5 text-right font-mono">
                      {([['S', c.s], ['A', c.a]] as const).map(([tag, figure]) => {
                        const value = figure?.contributors.find((x) => x.propertyId === p.id)?.value
                        return (
                          <div key={tag} className={value == null ? 'text-stone-700' : 'text-stone-200'}>
                            <span className="mr-1 text-[8px] text-stone-600">{tag}</span>
                            {value == null ? DASH : formatRolled(value, c, false)}
                          </div>
                        )
                      })}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-1 text-[8.5px] text-stone-600">S: {sideLabels.sponsor}. A: {sideLabels.underwriter}.</p>
        </section>
      )}
    </div>
  )
}

type Row = {
  field: string
  label: string
  kind: string
  unit: string | null
  group: string
  s?: RolledFigure
  a?: RolledFigure
}

function GroupRows({
  group,
  showGroup,
  columns,
  rows,
  variant,
  exact,
  sides,
  properties,
  open,
  onToggle,
}: {
  group: string
  showGroup: boolean
  columns: number
  rows: Row[]
  variant: 'summary' | 'full'
  exact: boolean
  sides: RollupSide[]
  properties: { id: string; name: string; location: string | null }[]
  open: Set<string>
  onToggle: (field: string) => void
}) {
  return (
    <>
      {showGroup ? (
        <tr>
          <td colSpan={columns} className="pb-0.5 pt-2 font-mono text-[8px] uppercase tracking-widest text-stone-600">
            {group}
          </td>
        </tr>
      ) : null}
      {rows.map((row) => {
        const expanded = open.has(row.field)
        return (
          <RowFragment key={row.field}>
            <tr className="border-t border-stone-900 align-top">
              <td className="px-2 py-1.5 text-stone-300">
                {variant === 'full' ? (
                  <button
                    type="button"
                    aria-expanded={expanded}
                    aria-label={`Show sources for ${row.label}`}
                    onClick={() => onToggle(row.field)}
                    className="mr-1 inline-flex align-middle text-stone-600 hover:text-amber-300"
                  >
                    {expanded ? <ChevronDown size={11} aria-hidden /> : <ChevronRight size={11} aria-hidden />}
                  </button>
                ) : null}
                <span>{row.label}</span>
              </td>
              <td className="px-2 py-1.5">
                <FigureCell figure={row.s} exact={exact} showMethod={variant === 'full'} />
              </td>
              <td className="px-2 py-1.5">
                <FigureCell figure={row.a} exact={exact} showMethod={variant === 'full'} />
              </td>
              {variant === 'full' ? (
                <td className="px-2 py-1.5 font-mono text-stone-300">{difference(row.s, row.a)}</td>
              ) : null}
            </tr>
            {expanded ? (
              <tr>
                <td colSpan={columns} className="px-2 pb-2">
                  <div role="region" aria-label={`Sources for ${row.label}`} className="space-y-1 rounded border border-stone-800 bg-black/25 p-2 text-[9.5px]">
                    {properties.map((p) => (
                      <div key={p.id} className="grid gap-1 md:grid-cols-[150px_minmax(0,1fr)_minmax(0,1fr)]">
                        <div className="text-stone-300">
                          {p.name}
                          <div className="text-[8.5px] text-stone-600">{p.location}</div>
                        </div>
                        {sides.map((side) => (
                          <div key={side.role}>
                            <span className="mr-1 text-[8px] uppercase text-stone-600">{side.label}</span>
                            <Provenance figure={side.role === 'sponsor' ? row.s : row.a} side={side} propertyId={p.id} />
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </td>
              </tr>
            ) : null}
          </RowFragment>
        )
      })}
    </>
  )
}

/** A keyed fragment, so a row and its detail row stay together. */
function RowFragment({ children }: { children: React.ReactNode }) {
  return <>{children}</>
}
